import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useDrawer from "../hooks/useDrawer";
import { DataGrid, Drawer } from "devextreme-react";
import { handleCellStyle, toastMsg } from "../utils";
import {
  Column,
  Editing,
  FilterRow,
  HeaderFilter,
  LoadPanel,
  MasterDetail,
  Scrolling,
  Sorting,
  Toolbar,
  Item,
  Button,
  Lookup,
} from "devextreme-react/data-grid";
import IssueFormPopup from "./IssueFormPopup";
import AuditTrailsGrid from "./AuditTrailsGrid";
import axios from "axios";
import CustomStore from "devextreme/data/custom_store";
import { useSelector } from "react-redux";
import { confirm } from "devextreme/ui/dialog";
import { getReportDivision } from "../access";

const IssuesDetailsWrapper = styled.div`
  width: 100%;
  h3 {
    margin: 0;
    font-size: 1em;
  }
  .dx-button {
    margin: 0 4px;
  }
  > div:first-child {
    display: flex;
  }
  #issueDetailsGrid {
    max-width: 99vw;
  }
`;

const DetailViewContainer = styled.div`
  display: grid;
  height: 100%;
  grid-template-columns: 50px 200px 750px repeat(2, 1fr);
  grid-template-rows: repeat(auto-fill, auto);
  grid-gap: 2px;
  white-space: pre-wrap;
  .issue__title {
    font-weight: bold;
    background-color: #e2efda;
  }

  div {
    padding: 8px 16px;
    border-radius: 2px;
    background-color: white;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    &.issue__content {
      justify-content: flex-start;
      text-align: left;
    }
  }
`;

const IssuesDetails = ({
  contrNo,
  isEditable,
  projectListRef,
  setMode,
  yearMonth,
}) => {
  const [auditTrailsDataSource, setAuditTrailsDataSource] = useState(null);
  const [{ isNewRecord, formData, isPopupVisible }, setIssueFormState] =
    useState({});
  const [issueDataSource, setIssueDataSource] = useState(null);
  const { isDrawerVisible, toggleDrawer } = useDrawer();
  const issueDataGridRef = useRef(null);
  const userInfo = useSelector((state) => state.userInfo);
  const reportDiv = getReportDivision(userInfo.div);

  useEffect(() => {
    const requestConfig = { params: { yearMonth } };
    setIssueDataSource(
      new CustomStore({
        key: "id",
        //! api GET R15. Get the issues
        load: async () => {
          const { data } = await axios.get(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/${yearMonth}/${contrNo}`
          );

          return data;
        },
        insert: async ({ formData, cuzNAcArr }) => {
          console.log(`start:`, cuzNAcArr);
          //! api POST C5-1. Create an issue
          const {
            data: { id },
            //! userInfo
          } = await axios.post(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/${contrNo}/${reportDiv}`,
            {
              iss: formData,
              employeeNo: userInfo.employeeNo,
            },
            requestConfig
          );

          //! api POST C5-2. Create a cause and action of the issue
          // 배열을 수정할 수 있는 for 루프 사용
          for (let i = 0; i < cuzNAcArr.length; i++) {
            const { order, ...cuzNAc } = cuzNAcArr[i];
            const response = await axios.post(
              `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/cuzNAc/${contrNo}/${id}`,
              { cuzNAc, employeeNo: userInfo.employeeNo },
              requestConfig
            );
            const { order: newOrder } = response.data;
            // 원본 배열의 항목을 직접 업데이트
            cuzNAcArr[i] = { ...cuzNAc, order: newOrder };
          }
          return { id, ...formData, cuzNAcArr };
        },

        update: async (id, { formData, cuzNAcArr, newCuzNAc }) => {
          //! api PUT U6-1. Update the issue
          await axios.put(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/${contrNo}/${id}`,
            {
              iss: formData,
              employeeNo: userInfo.employeeNo,
            },
            requestConfig
          );
          for (let i = 0; i < cuzNAcArr.length; i++) {
            const { order, ...cuzNAc } = cuzNAcArr[i];
            if (cuzNAc.status && cuzNAc.status === "deleted") {
              //! api DELETE D2. Delete the cause and action of issue
              await axios.delete(
                `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/cuzNAc/${contrNo}/${id}/${order}`,
                requestConfig
              );
            } else {
              //! api PUT U6-2. Update the cause and action of issue
              await axios.put(
                `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/cuzNAc/${contrNo}/${id}/${order}`,
                { cuzNAc, employeeNo: userInfo.employeeNo },
                requestConfig
              );
            }
          }
          //! api POST C5-2. Create a cause and action of the issue
          for (let i = 0; i < newCuzNAc.length; i++) {
            const { order, ...cuzNAc } = newCuzNAc[i];
            await axios.post(
              `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/cuzNAc/${contrNo}/${id}`,
              { cuzNAc, employeeNo: userInfo.employeeNo },
              requestConfig
            );
          }
        },

        //! api DELETE D1. Delete the issue
        remove: async (id) => {
          await axios.delete(
            `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/${contrNo}/${id}`,
            requestConfig
          );
          toastMsg(`${id}가 정상적으로 삭제되었습니다`, true);
        },
        errorHandler: (error) => {
          toastMsg(`에러가 발생했습니다. 관리자에게 문의해주세요.`, false);
          console.log(error);
        },
      })
    );
  }, [contrNo, yearMonth, userInfo.employeeNo, reportDiv]);

  const handleAddRow = () => {
    setIssueFormState({
      isNewRecord: true,
      formData: {},
      isPopupVisible: true,
    });
  };

  const handleCalculateSortValue = (e) => {
    return e.sortOrder;
  };

  const handleClickATBtn = (e) => {
    const issueId = e.row.data.id;
    //! api R16. Get the audit trails of issue
    axios
      .get(
        `${process.env.REACT_APP_BECCO_API_URL}/api/mthlyReport/proj/iss/AT/${contrNo}/${issueId}`
      )
      .then((res) => {
        if (Array.isArray(res.data)) {
          const auditTrailData = res.data
            .reverse() // modifiedData를 최신순으로 나열하기 위함.
            .reduce(
              (acc, { employeeNo, name, date, item, oldValue, newValue }) => {
                // 복합키로 employeeNo와 date의 조합을 확인합니다.
                const key = `${employeeNo}-${date}`;

                // 이미 처리된 키인지 확인합니다.
                const existingEntry = acc.find(
                  (entry) => `${entry.employeeNo}-${entry.date}` === key
                );

                if (existingEntry) {
                  // 기존에 존재하는 경우, 해당 항목에 data를 추가합니다.
                  existingEntry.modifiedData.push({ item, oldValue, newValue });
                } else {
                  // 새로운 항목인 경우, acc 배열에 새 객체를 추가합니다.
                  acc.push({
                    employeeNo,
                    name,
                    date,
                    modifiedData: [{ item, oldValue, newValue }],
                  });
                }

                return acc;
              },
              []
            );
          setAuditTrailsDataSource(auditTrailData.reverse()); // 최신순 배열.
          toggleDrawer();
        }
      });
  };

  const handleClickMode = () => {
    setMode("read");
  };

  const handleDeleteRow = async (e) => {
    const result = await confirm(
      `${e.row.data.id}를 삭제하시겠습니까?`,
      "Delete Confirmation"
    );

    if (result) {
      issueDataSource.remove(e.row.data.id).then((id) => {
        issueDataSource.push([{ type: "remove", key: id }]);
        projectListRef.current.instance.refresh(); // 이슈 개수 업데이트
      });
    }
  };

  const handleEditRow = (e) => {
    setIssueFormState({
      isNewRecord: false,
      formData: { ...e.row.data },
      isPopupVisible: true,
    });
  };

  const handleHidePopup = () => {
    setIssueFormState({ isPopupVisible: false });
  };

  const handleOutSideClick = (e) => {
    if (e.target.textContent !== "Audit Trails") {
      toggleDrawer(false);
      return true;
    }
  };

  const isRowNotDeletable = (e) => {
    const canBeDeleted = e.row.data.canBeDeleted === "Y";
    return !canBeDeleted;
  };

  const renderAuditTrails = () => {
    return (
      <AuditTrailsGrid
        dataSource={auditTrailsDataSource}
        renderAuditTrailsDetailView={renderAuditTrailsDetailView}
      />
    );
  };

  const renderAuditTrailsDetailView = (e) => {
    return (
      <div>
        <h3>Detail</h3>
        <DataGrid
          allowColumnResizing={true}
          columnResizingMode={"nextColumn"}
          dataSource={e.data.modifiedData}
          onCellPrepared={handleCellStyle}
          showBorders={true}
          showColumnLines={true}
          wordWrapEnabled={true}
        >
          <Column
            dataField="item"
            alignment="center"
            caption="Item"
            width={100}
          />
          <Column dataField="oldValue" alignment="center" caption="Old Value" />
          <Column dataField="newValue" alignment="center" caption="New Value" />
        </DataGrid>
      </div>
    );
  };

  const renderEmployeeName = (e) => {
    // 이름 (사번) 형식으로 렌더링한다.
    return `${e.oName} (${e.oEmployeeNo})`;
  };

  const renderEBEmployeeName = (e) => {
    return `${e.eBName} (${e.eBEmployeeNo})`;
  };

  const renderIssuesDetailView = (e) => {
    return (
      <DetailViewContainer>
        <div className="issue__title" style={{ gridColumn: "1/3" }}>
          Issue Description
        </div>
        <div className="issue__content" style={{ gridColumn: "3/-1" }}>
          {e.data.issDescr}
        </div>

        <div className="issue__title" style={{ gridColumn: "1/3" }}>
          Impact Summary
        </div>
        <div className="issue__content" style={{ gridColumn: "3/-1" }}>
          {e.data.iSum}
        </div>
        <div className="issue__title">No.</div>
        <div className="issue__title">Root Cause</div>
        <div className="issue__title">Action</div>
        <div className="issue__title">Expected Resolution Date</div>
        <div className="issue__title">Actual Resolution Date</div>
        {e.data.cuzNAcArr.map((el, idx) => {
          return (
            <>
              <div key={`no${el.order}`}>{idx + 1}</div>
              <div key={`cause${el.order}`}>{el.rootCuz}</div>
              <div key={`action${el.order}`} className="issue__content">
                {el.ac}
              </div>
              <div key={`expdate${el.order}`}>{el.expResDate}</div>
              <div key={`actdate${el.order}`}>{el.actResDate}</div>
            </>
          );
        })}
        <div className="issue__title" style={{ gridColumn: "1/3" }}>
          Final Resolution & Rationale
        </div>
        <div className="issue__content" style={{ gridColumn: "3/-1" }}>
          {e.data.fResR}
        </div>
      </DetailViewContainer>
    );
  };

  return (
    <IssuesDetailsWrapper>
      <h3>Issues Details</h3>
      <Drawer
        closeOnOutsideClick={handleOutSideClick}
        height={590}
        opened={isDrawerVisible}
        openedStateMode="overlap"
        position="right"
        render={renderAuditTrails}
        reavelMode="slide"
        shading={true}
        width={"100%"}
      >
        <DataGrid
          allowColumnResizing={true}
          columnResizingMode={"nextColumn"}
          dataSource={issueDataSource}
          height={"100%"}
          id="issueDetailsGrid"
          onCellPrepared={handleCellStyle}
          ref={issueDataGridRef}
          showBorders={true}
          showColumnLines={true}
          width={"100%"}
          wordWrapEnabled={true}
        >
          <Editing
            allowAdding={isEditable}
            allowDeleting={isEditable}
            allowUpdating={isEditable}
            mode="popup"
            refreshMode="full"
          />
          <FilterRow visible={true} />
          <HeaderFilter visible={true} />
          <LoadPanel enabled={true} />
          <Scrolling mode="virtual" showScrollbar={"always"} />
          <Sorting mode="multiple" />
          <Toolbar>
            <Item
              name="addRowButton"
              location="after"
              options={{ icon: "plus", onClick: handleAddRow }}
              widget="dxButton"
            />
            <Item
              name="revertBtn"
              location="after"
              options={{ icon: "revert", onClick: handleClickMode }}
              widget="dxButton"
            />
          </Toolbar>
          <Column dataField="id" alignment="center" caption="id" width={100} />
          <Column
            dataField="statName"
            alignment="center"
            calculateSortValue={handleCalculateSortValue}
            caption="Status"
            sortIndex={0}
            sortOrder="asc"
            minWidth={150}
          >
            {/* <Lookup dataSource={issueStatusDataSource} displayExpr="name" valueExpr="codeId" /> */}
          </Column>
          <Column
            dataField="gradDescr"
            alignment="center"
            caption="Grade"
            minWidth={100}
          >
            {/* <Lookup dataSource={issueGradeDataSource} displayExpr="name" valueExpr="codeId" /> */}
          </Column>
          <Column
            dataField="typeName"
            alignment="center"
            caption="Type"
            minWidth={100}
          >
            {/* <Lookup dataSource={issueTypeDataSource} displayExpr="name" valueExpr="codeId" /> */}
          </Column>
          <Column
            dataField="intShr"
            alignment="center"
            caption="Internal Share"
            dataType="string"
            minWidth={110}
          />
          <Column
            dataField="oEmployeeNo"
            alignment="center"
            calculateCellValue={renderEmployeeName}
            caption="Owner"
            minWidth={150}
          >
            {/* <Lookup dataSource={participantsDataSource} displayExpr="auName" valueExpr="au" /> */}
          </Column>
          <Column
            dataField="iDate"
            alignment="center"
            caption="Date Identified"
            dataType="date"
            format="yyyy-MM-dd"
            sortIndex={1}
            sortOrder="desc"
            minWidth={130}
          />
          <Column
            dataField="eBEmployeeNo"
            alignment="center"
            calculateCellValue={renderEBEmployeeName}
            caption="Entered By"
            minWidth={150}
          >
            {/* <Lookup dataSource={participantsDataSource} displayExpr="auName" valueExpr="au" /> */}
          </Column>
          <Column
            dataField="assocdId"
            alignment="center"
            caption="Associated Id"
            minWidth={100}
          />
          <Column type="buttons" width={90}>
            <Button name="edit" onClick={handleEditRow} />
            <Button
              disabled={isRowNotDeletable}
              name="delete"
              onClick={handleDeleteRow}
            />
            <Button
              hint="Audit Trails"
              name="auditTrailsButton"
              onClick={handleClickATBtn}
              text="AT"
            />
          </Column>
          <MasterDetail enabled={true} render={renderIssuesDetailView} />
        </DataGrid>
      </Drawer>
      {isPopupVisible && (
        <IssueFormPopup
          contrNo={contrNo}
          formData={formData}
          handleHidePopup={handleHidePopup}
          isNewRecord={isNewRecord}
          isPopupVisible={isPopupVisible}
          issueDataGridRef={issueDataGridRef}
          projectListRef={projectListRef}
          yearMonth={yearMonth}
        />
      )}
    </IssuesDetailsWrapper>
  );
};

export default IssuesDetails;
